import { z } from 'zod';
/**
 * Request payload schemas, shared by the API (which enforces them) and the
 * frontend (which uses them for form validation). One definition means a field
 * the server rejects is a field the form flagged first.
 */
const trimmed = (max) => z.string().trim().min(1).max(max);
/**
 * An optional text field, accepting every spelling of "not set".
 *
 * The API serves an unset column as `null`, so a client that reads a record,
 * changes one field, and writes the whole thing back — the obvious way to use
 * a REST API, and what the Settings form does — sends `null` straight back.
 * A schema that only accepted `string | undefined` rejected that with a 400 on
 * a field the user never touched.
 *
 * Empty string and null both normalize to null, so "cleared" has one
 * representation in the database rather than two.
 */
const optionalText = (max) => z
    .union([z.string().trim().max(max), z.null()])
    .optional()
    .transform((value) => (value === undefined ? undefined : value === null || value === '' ? null : value));
/** US state code. Carriers reject anything else, so normalize early and loudly. */
const stateCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Must be a 2-letter state code');
/** 12345 or 12345-6789. */
const zipCode = z
    .string()
    .trim()
    .regex(/^\d{5}(-\d{4})?$/, 'Must be a 5-digit ZIP, optionally +4');
const countryCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Must be a 2-letter country code')
    .default('US');
/**
 * Phone number. USPS will not return rates without one on the sender, which is
 * why this is required rather than optional wherever it appears.
 */
const phone = z
    .string()
    .trim()
    .min(7, 'Too short to be a phone number')
    .max(32)
    .refine((v) => (v.match(/\d/g) ?? []).length >= 7, 'Needs at least 7 digits');
/** Inches. Upper bounds are generous but catch a misplaced decimal point. */
const dimension = z.coerce.number().positive('Must be greater than 0').max(999);
// ---------------------------------------------------------------------------
// App settings — the sender identity for bulk runs
// ---------------------------------------------------------------------------
export const appSettingsInputSchema = z.object({
    senderName: trimmed(120),
    senderPhone: phone,
    /**
     * Contact address printed on every label the shop ships.
     *
     * Required, because Shippo refuses a purchase when the from-address has no
     * email. A shop address rather than a person's: an employee leaving should
     * not orphan the contact on labels already in the post.
     */
    senderEmail: z.string().trim().toLowerCase().email('Enter a valid email address').max(200),
    senderAddress1: trimmed(200),
    senderAddress2: optionalText(200),
    senderCity: trimmed(100),
    senderState: stateCode,
    senderZip: zipCode,
    senderCountry: countryCode,
});
// ---------------------------------------------------------------------------
// Shipping boxes
// ---------------------------------------------------------------------------
export const shippingBoxInputSchema = z.object({
    name: trimmed(80),
    length: dimension,
    width: dimension,
    height: dimension,
});
// ---------------------------------------------------------------------------
// Media — what a label gets printed on
// ---------------------------------------------------------------------------
export const mediaTypeSchema = z.enum(['SHIPPING_LABEL', 'ENVELOPE']);
export const mediaInputSchema = z.object({
    name: trimmed(80),
    type: mediaTypeSchema,
    width: dimension,
    height: dimension,
});
// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------
export const returnAddressInputSchema = z.object({
    name: trimmed(80),
    contactName: optionalText(120),
    phone,
    address1: trimmed(200),
    address2: optionalText(200),
    city: trimmed(100),
    state: stateCode,
    zip: zipCode,
    country: countryCode,
    isDefault: z.boolean().optional(),
});
export const deliveryAddressInputSchema = z.object({
    name: trimmed(120),
    // Remembered with the address so a customer who needed a phone number last
    // time does not need it typed again.
    phone: optionalText(32),
    email: optionalText(200),
    address1: trimmed(200),
    address2: optionalText(200),
    city: trimmed(100),
    state: stateCode,
    zip: zipCode,
    country: countryCode,
});
// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------
/**
 * Minimum password length.
 *
 * Six, chosen for a shop where staff sign in at a counter all day and the app
 * is reachable only from the shop network.
 *
 * Short passwords are weak in two ways, and only one of them is addressed:
 * online guessing is throttled hard at the sign-in endpoint (see the rateLimit
 * block in auth.ts), so working through six characters over the network is not
 * practical. Offline attack is not addressed and cannot be — anyone who obtains
 * a copy of the database can grind six characters quickly, whatever the hashing.
 * That risk is carried by keeping the database off the internet and backed up
 * somewhere access-controlled.
 */
export const MIN_PASSWORD_LENGTH = 6;
export const createUserInputSchema = z.object({
    name: trimmed(120),
    email: z.string().trim().toLowerCase().email(),
    password: z.string().min(MIN_PASSWORD_LENGTH, `At least ${MIN_PASSWORD_LENGTH} characters`),
    role: z.enum(['admin', 'staff']).default('staff'),
});
//# sourceMappingURL=entities.js.map